import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type ReactNode,
} from "react";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface SearchInputProps {
  /** Committed value (e.g. the active keyword); syncs the field on external resets. */
  value: string;
  /** Fired after the user pauses typing (debounced). */
  onSearch: (value: string) => void;
  placeholder?: string;
  debounceMs?: number;
  className?: string;
  /** Rendered inside the field, right-aligned (e.g. a result count). */
  adornment?: ReactNode;
}

type SearchFieldProps = Omit<ComponentPropsWithoutRef<typeof Input>, "type"> & {
  wrapperClassName?: string;
  adornment?: ReactNode;
};

export function SearchField({
  className,
  wrapperClassName,
  adornment,
  ...props
}: SearchFieldProps) {
  const slot = useRef<HTMLSpanElement>(null);
  const [slotWidth, setSlotWidth] = useState(0);

  useLayoutEffect(() => {
    setSlotWidth(adornment == null ? 0 : (slot.current?.offsetWidth ?? 0));
  }, [adornment]);

  return (
    <div className={cn("relative", wrapperClassName)}>
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      <Input
        type="search"
        spellCheck={false}
        {...props}
        style={slotWidth ? { paddingRight: slotWidth + 24 } : props.style}
        className={cn("w-full pl-9", className)}
      />
      {adornment != null && (
        <span
          ref={slot}
          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm tabular-nums text-muted-foreground"
        >
          {adornment}
        </span>
      )}
    </div>
  );
}

export function SearchInput({
  value,
  onSearch,
  placeholder = "Search…",
  debounceMs = 300,
  className,
  adornment,
}: SearchInputProps) {
  const [text, setText] = useState(value);
  useEffect(() => {
    setText(value);
  }, [value]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  function handleChange(next: string) {
    setText(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => onSearch(next), debounceMs);
  }

  return (
    <SearchField
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      placeholder={placeholder}
      adornment={adornment}
      wrapperClassName={cn("ml-auto w-80 shrink-0", className)}
    />
  );
}
